import { useEffect, useRef, useCallback } from "react";
import { connectSocket } from "@/lib/api/client";
import { tabStore } from "@/lib/tab-store";
import { authStore } from "@/lib/auth-store";
import { toast } from "sonner";

// ── Action labels for notifications ──────────────────────────────────────────

const ACTION_LABELS: Record<string, string> = {
  create: "New tab opened",
  addItem: "Items added to tab",
  batchAddItems: "Items added to tab",
  pay: "Payment recorded",
  close: "Tab closed",
  issue: "Items issued",
  issueItem: "Item issued",
};

// ── Debounce helper ──────────────────────────────────────────────────────────

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function debouncedRefresh(fetcher: () => Promise<void>, delay = 300) {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => {
    fetcher();
    refreshTimer = null;
  }, delay);
}

// ── Local action tracking (suppress self-notifications) ──────────────────────

let lastLocalActionAt = 0;
const SELF_SUPPRESS_MS = 1500;

/**
 * Call this after performing a local action that will also trigger a socket event.
 * The next socket event within SELF_SUPPRESS_MS will be treated as "from self"
 * and will not show a toast notification.
 */
export function markLocalAction() {
  lastLocalActionAt = Date.now();
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Hook that connects to the Socket.IO server and listens for tab-related
 * real-time updates. When a `tabs:updated` event arrives from another client,
 * it refreshes the tab store and shows a toast notification.
 *
 * Use this in any page that displays tab data (tabs-admin, tabs-waiter, etc.).
 */
export function useRealtimeTabs() {
  const shownRef = useRef(false);

  const handleTabsUpdated = useCallback(
    (data: { action: string; id?: string; tabId?: string }) => {
      const user = authStore.getState().user;
      if (!user) return;

      // Determine which fetcher to use based on role
      const fetcher =
        user.role === "cashier" ||
        user.role === "admin" ||
        user.role === "manager"
          ? () => tabStore.initStaff()
          : user.role === "waiter"
            ? () => tabStore.initWaiter()
            : () => tabStore.init();

      // Debounce the refresh to avoid multiple rapid API calls
      debouncedRefresh(fetcher);

      // Only show notification for events from OTHER clients
      const isFromSelf = Date.now() - lastLocalActionAt < SELF_SUPPRESS_MS;
      if (!isFromSelf) {
        const label = ACTION_LABELS[data.action] || "Tab updated";
        toast.info(label, {
          duration: 3000,
        });
      }
    },
    [],
  );

  useEffect(() => {
    let socket: any = null;
    let cancelled = false;

    async function setup() {
      socket = await connectSocket();
      if (!socket || cancelled) return;

      socket.on("tabs:updated", handleTabsUpdated);
    }

    setup();

    return () => {
      cancelled = true;
      if (socket) {
        socket.off("tabs:updated", handleTabsUpdated);
      }
    };
  }, [handleTabsUpdated]);
}
